import React from "react";
import theme from "../../data/theme";

/**
 * ConfirmDestroy — the speed bump in front of anything that erases a board.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 WHY THIS EXISTS AS A SHARED COMPONENT. Aug 12 2026, a five-pick live board
 * was destroyed with no warning: boards are keyed by league name and setup
 * wrote straight through an existing one. The board is the only artifact in
 * this product that cannot be regenerated — sixteen slots times ten teams,
 * typed by hand, live, with eleven people waiting — and it was the only thing
 * with nothing in front of its destruction.
 *
 * So every destructive path routes through one component, and the rule it
 * encodes is:
 *
 *   🔴 THE COUNT IS ALWAYS NAMED. "Are you sure?" is not a confirmation; it is
 *   a keystroke. "Smithtown Auction has 5 picks entered" is the only version
 *   that can stop somebody who has two near-identically-named leagues — which
 *   is exactly how the wrong one got reached.
 *
 * ⚠️ CANCEL IS THE DEFAULT AND THE SAFE SIDE IS THE PLAIN BUTTON. The
 * destructive action is the RED one and it is never focused on open; Escape and
 * a click outside both cancel. Nothing here is a one-key confirm.
 * ══════════════════════════════════════════════════════════════════════
 */
export default function ConfirmDestroy({ title, body, confirmLabel, onConfirm, onCancel }) {
  return (
    <div
      data-testid="confirm-destroy"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
      style={{
        position: "fixed", inset: 0, background: theme.helpers.overlay(0.7), zIndex: 300,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div style={{
        background: theme.colors.extended.modalBg,
        border: `1px solid ${theme.colors.status.danger}`,
        borderRadius: theme.radius.xl, padding: 26, maxWidth: 440,
      }}>
        <div style={{
          fontSize: theme.fontSize.heading, fontWeight: theme.fontWeight.heavy,
          color: theme.colors.text.primary, marginBottom: 10,
        }}>{title}</div>

        <div style={{
          color: theme.colors.text.muted, fontSize: theme.fontSize.body,
          lineHeight: 1.55, marginBottom: 20,
        }}>{body}</div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          {/* 🔴 CANCEL FIRST IN THE DOM, so it is the first thing tab reaches
              and the first thing a screen reader announces in this row. */}
          <button
            autoFocus
            onClick={onCancel}
            style={{
              background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}`,
              borderRadius: theme.radius.md, color: theme.colors.text.primary,
              fontFamily: "inherit", fontSize: theme.fontSize.body, padding: "9px 16px",
              cursor: "pointer",
            }}
          >Cancel</button>
          <button
            onClick={onConfirm}
            style={{
              background: theme.colors.status.danger, border: "none",
              borderRadius: theme.radius.md, color: "#fff", fontFamily: "inherit",
              fontSize: theme.fontSize.body, fontWeight: theme.fontWeight.bold,
              padding: "9px 16px", cursor: "pointer",
            }}
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
